import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { cutToast } from 'utility/toast';
import React from 'react';

import { BlockBlobClient } from '@azure/storage-blob';
import { UploadFiles } from 'common/components/UploadFiles';
import { DocumentTypeOption } from 'common/components/UploadFiles/view';
import { OnDocumentUploadStatus } from 'common/graphql/__generated__/DocumentUploadStatusSubscription';

import {
  ApprovalExtendedDocumentTypes,
  ApprovalExtendedDocumentTypesVariables,
} from './__generated__/ApprovalExtendedDocumentTypes';
import {
  ApprovalUploadDocument,
  ApprovalUploadDocument_approvalUploadDocument,
  ApprovalUploadDocumentVariables,
} from './__generated__/ApprovalUploadDocument';

import APPROVAL_UPLOAD_DOCUMENT from './ApprovalUploadDocument.graphql';
import AVAILABLE_DOCUMENT_TYPES from './ApprovalExtendedDocumentTypes.graphql';
import DOCUMENT_UPLOAD_STATUS from 'common/graphql/DocumentUploadStatusSubscription.graphql';

interface UploadFormProps {
  entityApprovalId: string;
  onRefetch: () => void;
}
export const UploadForm: React.FC<UploadFormProps> = ({
  entityApprovalId,
  onRefetch,
}) => {
  const client = useApolloClient();

  const onUpload = async (
    fileSelected: File,
    document: ApprovalUploadDocument_approvalUploadDocument,
    toastId: string
  ) => {
    const observer = client.subscribe({
      query: DOCUMENT_UPLOAD_STATUS,
      variables: {
        documentId: document.document._documentFileId!,
      },
    });
    const subscription = observer.subscribe((data) => {
      const subscribedData = data.data as OnDocumentUploadStatus;
      const { status, document } = {
        ...subscribedData.documentUploadStatus,
      };
      if (!document) {
        if (status.type === 'VALIDATING') {
          cutToast.loading(
            status.message
              ? `Validating files ${fileSelected.name} - ${status.message}`
              : `Validating files ${fileSelected.name}`,
            { id: toastId }
          );
        } else if (status.type === 'EXTRACTING') {
          cutToast.loading(
            status.message
              ? `Extracting data from ${fileSelected.name} - ${status.message}`
              : `Extracting data from ${fileSelected.name}`,
            { id: toastId }
          );
        } else if (status.type === 'FAILURE') {
          subscription.unsubscribe();
          cutToast.error(
            status.message
              ? `Upload of ${fileSelected.name} failed - ${status.message}`
              : `Upload of ${fileSelected.name} failed`,
            { id: toastId }
          );
        } else if (status.type === 'WARNING') {
          subscription.unsubscribe();
          cutToast.warning(
            status.message
              ? `Warning for file ${fileSelected.name}: ${status.message}`
              : `Warning for file ${fileSelected.name}`,
            { id: toastId }
          );
        }
      } else {
        subscription.unsubscribe();
        onRefetch?.();
        cutToast.success(
          status.message
            ? `Successfully uploaded ${fileSelected.name}: ${status.message}`
            : `Successfully uploaded ${fileSelected.name}`,
          { id: toastId }
        );
      }
    });
  };

  const [uploadDocument] = useMutation<
    ApprovalUploadDocument,
    ApprovalUploadDocumentVariables
  >(APPROVAL_UPLOAD_DOCUMENT);

  const { data: AvailableDocumentTypes } = useQuery<
    ApprovalExtendedDocumentTypes,
    ApprovalExtendedDocumentTypesVariables
  >(AVAILABLE_DOCUMENT_TYPES, {
    variables: {
      entityApprovalId: entityApprovalId,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });
  const documentTypeOptions =
    AvailableDocumentTypes?.approvalExtendedDocumentTypes?.nodes?.map(
      (doctype) =>
        ({
          key: doctype.id,
          text: doctype.documentType || '',
          isAccountingDocument: doctype.isAccountingDocument,
          extractionTypes: doctype.extractionTypes,
        }) as DocumentTypeOption
    ) || [];

  return (
    <UploadFiles
      documentTypes={documentTypeOptions!}
      uploadDocument={{
        uploadDocumentData: async (documentType, data, fileSelected) => {
          fileSelected.map(async (fileEntity, fileIndex) => {
            // const toastId = `file.name.${fileEntity?.name}.${fileIndex}`;
            const toastId = cutToast.info(`Uploading ${fileEntity?.name}...`);

            const uploadMutationResults = await uploadDocument({
              variables: {
                document: {
                  ...data,
                  documentTypeId: parseInt(documentType.key.toString()),
                  filename: fileEntity.name,
                },
                approvalId: entityApprovalId,
              },
            });

            if (uploadMutationResults.errors)
              cutToast.error(`Upload of ${fileEntity.name} failed`, {
                id: toastId,
              });

            if (
              uploadMutationResults.data?.approvalUploadDocument?.document &&
              uploadMutationResults.data.approvalUploadDocument.document
                ._documentFileId
            ) {
              onUpload?.(
                fileEntity,
                uploadMutationResults.data?.approvalUploadDocument,
                toastId!
              );

              const client = new BlockBlobClient(
                uploadMutationResults.data?.approvalUploadDocument?.uploadLink
              );
              await client.uploadData(fileEntity);
            }
          });
        },
      }}
    />
  );
};
